import {
  Add,
  DeleteOutline,
  Gavel,
  AccountBalance,
  OpenInNew,
} from '@mui/icons-material';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import React, { useState } from 'react';
import { ApiError } from '../../hooks/useFetch';
import { useAlert } from '../../context/AlertContext';
import type { ICourt } from '../../Interfaces/ICourt';
import type { IAuthority } from '../../Interfaces/IAuthority';
import type { ICourtRole } from '../../Interfaces/ICourtRole';
import type { AemterNavTarget, AemterNavigate } from './aemterNav';

/**
 * Wiederverwendbare Detail-Karte: ordnet einem Anker (politische Gemeinde ODER
 * Postleitzahl) Gerichte/Behörden je Rolle zu. Fachlich hängen hier alle Rollen
 * AUSSER Grundbuchamt (das lebt an der Gemarkung).
 *
 * Die Komponente ist bewusst tabellen-agnostisch: der Aufrufer reicht die
 * bereits für diesen Anker gefilterten Zuordnungen sowie create/delete durch.
 * Pro (Anker, Rolle) ist nur EINE Stelle möglich (serverseitig zusätzlich per
 * 409 abgesichert).
 */
export type AnchorKind = 'municipality' | 'postcode';
type OfficeType = 'court' | 'authority';

// Normalisierte Sicht auf eine Zuordnung — unabhängig von der konkreten Tabelle.
export interface AnchorAssignmentView {
  idAssignment: number;
  idCourt?: number | null;
  idAuthority?: number | null;
  idCourtRole: number;
}

// Read-only abgeleitete Zuständigkeit (nicht an diesem Anker bearbeitbar):
//  - Gemeinde: die über die Gemarkungen summierten Grundbuchämter.
//  - PLZ: die über die Gemeinde geerbten Gerichte (ggf. per PLZ überschrieben).
export interface InheritedOffice {
  id: string;
  roleLabel: string;
  officeLabel: string;
  note: string;
  overridden?: boolean;
  navTarget?: AemterNavTarget;
}

interface IProps {
  anchorKind: AnchorKind;
  anchorName: string;
  anchorFederalState?: number | null;
  courts: ICourt[];
  authorities: IAuthority[];
  courtRoles: ICourtRole[];
  /** Alle (aktiven) Zuordnungen dieses Ankers — bereits gefiltert. */
  assignments: AnchorAssignmentView[];
  /** Welche Rollen-Kategorien hier zulässig sind. PLZ: nur Gerichte. */
  allowedCategories: OfficeType[];
  onCreate: (a: {
    idCourt: number | null;
    idAuthority: number | null;
    idCourtRole: number;
  }) => Promise<unknown>;
  onDelete: (idAssignment: number) => Promise<unknown>;
  isBusy?: boolean;
  onAnyChange: () => void;
  /** Kontextzeile über der Liste (z. B. „Zugeordnete Gemeinde: …" beim PLZ). */
  contextLine?: string;
  /** Sprungziel der Kontextzeile (z. B. zur zugeordneten Gemeinde). */
  contextNavTarget?: AemterNavTarget;
  /** Editor-Slot neben der Kontextzeile (z. B. „Gemeinde ändern" beim PLZ). */
  contextEditor?: React.ReactNode;
  /** Read-only Kontext-Chips (z. B. „Gemarkungen (über die Gemeinde)" beim PLZ). */
  contextChipsTitle?: string;
  contextChips?: { key: string; label: string; navTarget?: AemterNavTarget }[];
  /** Read-only abgeleitete Einträge + Überschrift. */
  inherited?: InheritedOffice[];
  inheritedTitle?: string;
  /** Sprung zu einem referenzierten Element (Gericht/Behörde/Gemeinde/…). */
  onNavigate?: AemterNavigate;
}

interface Office {
  type: OfficeType;
  id: number;
  name: string;
  idFederalState?: number;
}
const officeKey = (o: Office) => `${o.type}:${o.id}`;

const extractErrorText = (e: unknown, fallback: string): string => {
  if (
    e instanceof ApiError &&
    e.body &&
    typeof e.body === 'object' &&
    'fehler' in e.body
  ) {
    const f = (e.body as { fehler?: unknown }).fehler;
    if (typeof f === 'string' && f) return f;
  }
  return fallback;
};

export const AnchorOfficeAssignmentPane: React.FC<IProps> = ({
  anchorKind,
  anchorName,
  anchorFederalState,
  courts,
  authorities,
  courtRoles,
  assignments,
  allowedCategories,
  onCreate,
  onDelete,
  isBusy,
  onAnyChange,
  contextLine,
  contextNavTarget,
  contextEditor,
  contextChipsTitle,
  contextChips,
  inherited,
  inheritedTitle,
  onNavigate,
}) => {
  const { showAlert } = useAlert();
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerRoleId, setPickerRoleId] = useState<number | ''>('');
  const [pickerOffice, setPickerOffice] = useState<Office | null>(null);

  const allOffices: Office[] = [
    ...courts.map(c => ({
      type: 'court' as const,
      id: c.idCourt,
      name: c.Court,
      idFederalState: c.idFederalState,
    })),
    ...authorities.map(a => ({
      type: 'authority' as const,
      id: a.idAuthority,
      name: a.Authority,
      idFederalState: a.idFederalState,
    })),
  ].filter(o => allowedCategories.includes(o.type));

  const officeOfAssignment = (a: AnchorAssignmentView): Office | null => {
    if (a.idAuthority) {
      const au = authorities.find(x => x.idAuthority === a.idAuthority);
      return au
        ? { type: 'authority', id: au.idAuthority, name: au.Authority }
        : null;
    }
    if (a.idCourt) {
      const c = courts.find(x => x.idCourt === a.idCourt);
      return c ? { type: 'court', id: c.idCourt, name: c.Court } : null;
    }
    return null;
  };

  // Rollen, die an diesem Anker bereits vergeben sind (eine Stelle je Rolle).
  const usedRoleIds = new Set(assignments.map(a => a.idCourtRole));

  // Am Anker noch freie Rollen der erlaubten Kategorien (ohne Grundbuchamt),
  // alphabetisch, Gerichts-Rollen vor Behörden-Rollen.
  const availableRoles: ICourtRole[] = courtRoles
    .filter(
      r =>
        r.activeRole !== false &&
        allowedCategories.includes(r.RoleCategory as OfficeType) &&
        r.CourtRole !== 'Grundbuchamt' &&
        !usedRoleIds.has(r.idCourtRole)
    )
    .sort(
      (a, b) =>
        a.RoleCategory.localeCompare(b.RoleCategory) ||
        a.CourtRole.localeCompare(b.CourtRole)
    );

  // Gruppierung nach Amt.
  const grouped = new Map<string, AnchorAssignmentView[]>();
  for (const a of assignments) {
    const o = officeOfAssignment(a);
    if (!o) continue;
    const arr = grouped.get(officeKey(o)) ?? [];
    arr.push(a);
    grouped.set(officeKey(o), arr);
  }
  const usedKeys = Array.from(grouped.keys()).sort((a, b) =>
    (allOffices.find(o => officeKey(o) === a)?.name ?? a).localeCompare(
      allOffices.find(o => officeKey(o) === b)?.name ?? b
    )
  );

  const persist = async (
    officeType: OfficeType,
    officeId: number,
    idCourtRole: number
  ) => {
    try {
      await onCreate({
        idCourt: officeType === 'court' ? officeId : null,
        idAuthority: officeType === 'authority' ? officeId : null,
        idCourtRole,
      });
      showAlert({ text: 'Rolle hinzugefügt' });
      onAnyChange();
    } catch (e) {
      console.error(e);
      showAlert({
        severity: 'error',
        text: extractErrorText(e, 'Fehler beim Speichern'),
      });
    }
  };

  const handleRemove = async (idAssignment: number) => {
    try {
      await onDelete(idAssignment);
      showAlert({ text: 'Zuordnung entfernt' });
      onAnyChange();
    } catch (e) {
      console.error(e);
      showAlert({ severity: 'error', text: 'Fehler beim Entfernen' });
    }
  };

  // Gewählte Rolle bestimmt die Kategorie (Gericht/Behörde) der Stelle.
  const selectedRole = courtRoles.find(r => r.idCourtRole === pickerRoleId);

  // Stellen passend zur gewählten Rolle (Kategorie) im Bundesland des Ankers.
  const officeOptions: Office[] = allOffices
    .filter(
      o =>
        (!selectedRole || o.type === selectedRole.RoleCategory) &&
        (anchorFederalState === null ||
          anchorFederalState === undefined ||
          o.idFederalState === anchorFederalState)
    )
    .sort((a, b) => a.name.localeCompare(b.name));

  const openPicker = () => {
    setPickerRoleId('');
    setPickerOffice(null);
    setPickerOpen(true);
  };

  const submitPicker = async () => {
    if (typeof pickerRoleId !== 'number' || !pickerOffice) return;
    await persist(pickerOffice.type, pickerOffice.id, pickerRoleId);
    setPickerOpen(false);
  };

  const kindLabel = anchorKind === 'municipality' ? 'Gemeinde' : 'PLZ';

  const JumpButton: React.FC<{ target?: AemterNavTarget }> = ({ target }) =>
    onNavigate && target ? (
      <Tooltip title='Zum Element springen'>
        <IconButton size='small' onClick={() => onNavigate(target)}>
          <OpenInNew fontSize='inherit' />
        </IconButton>
      </Tooltip>
    ) : null;

  return (
    <Card variant='outlined'>
      <CardContent>
        <Stack
          direction='row'
          justifyContent='space-between'
          alignItems='center'
          sx={{ mb: 1 }}
        >
          <Typography variant='subtitle1' fontWeight={600}>
            {allowedCategories.includes('authority')
              ? `Gerichte & Behörden dieser ${kindLabel}`
              : `Gerichte dieser ${kindLabel}`}
          </Typography>
          <Button
            size='small'
            variant='outlined'
            startIcon={<Add />}
            onClick={openPicker}
            disabled={availableRoles.length === 0}
          >
            Stelle zuordnen
          </Button>
        </Stack>

        {contextLine && (
          <Stack
            direction='row'
            alignItems='center'
            spacing={0.5}
            sx={{ mb: 1, flexWrap: 'wrap' }}
          >
            <Typography variant='body2'>{contextLine}</Typography>
            <JumpButton target={contextNavTarget} />
            {contextEditor}
          </Stack>
        )}

        {contextChips && contextChips.length > 0 && (
          <Box sx={{ mb: 1 }}>
            <Typography variant='caption' color='text.secondary'>
              {contextChipsTitle ?? 'Zugeordnet'}
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
              {contextChips.map(c => (
                <Chip
                  key={c.key}
                  size='small'
                  variant='outlined'
                  clickable={!!(onNavigate && c.navTarget)}
                  onClick={
                    onNavigate && c.navTarget
                      ? () => onNavigate(c.navTarget!)
                      : undefined
                  }
                  label={c.label}
                />
              ))}
            </Box>
          </Box>
        )}

        <Typography
          variant='caption'
          color='text.secondary'
          sx={{ mb: 1.5, display: 'block' }}
        >
          {anchorKind === 'postcode'
            ? 'Direkte PLZ-Zuweisung — überschreibt je Rolle die über die Gemeinde geerbte Stelle (z. B. Randbezirke).'
            : 'Gilt für die gesamte Gemeinde; eine spezifischere PLZ-Zuweisung geht vor. Grundbuchamt wird über die Gemarkung gepflegt.'}
        </Typography>

        {usedKeys.length === 0 ? (
          <Alert severity='info' variant='outlined'>
            Noch keine Zuordnungen vorhanden.
          </Alert>
        ) : (
          <Stack spacing={1}>
            {usedKeys.map(key => {
              const office = allOffices.find(o => officeKey(o) === key);
              const cp = grouped.get(key) ?? [];
              return (
                <Box
                  key={key}
                  sx={{
                    p: 1.25,
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 1,
                  }}
                >
                  <Stack
                    direction='row'
                    alignItems='center'
                    spacing={1}
                    sx={{ mb: 1 }}
                  >
                    {key.startsWith('authority:') ? (
                      <AccountBalance fontSize='small' />
                    ) : (
                      <Gavel fontSize='small' />
                    )}
                    <Typography
                      variant='body2'
                      fontWeight={600}
                      sx={{ flex: 1 }}
                    >
                      {office?.name ?? key}
                    </Typography>
                    {office && (
                      <JumpButton
                        target={{ kind: office.type, id: office.id }}
                      />
                    )}
                  </Stack>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {cp.map(a => {
                      const role = courtRoles.find(
                        r => r.idCourtRole === a.idCourtRole
                      );
                      return (
                        <Chip
                          key={a.idAssignment}
                          label={role?.CourtRole ?? `Rolle ${a.idCourtRole}`}
                          size='small'
                          color='primary'
                          variant='outlined'
                          onDelete={() => void handleRemove(a.idAssignment)}
                          disabled={isBusy}
                          deleteIcon={
                            <Tooltip title='Zuordnung entfernen'>
                              <DeleteOutline />
                            </Tooltip>
                          }
                        />
                      );
                    })}
                  </Box>
                </Box>
              );
            })}
          </Stack>
        )}

        {inherited && inherited.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Typography
              variant='subtitle2'
              fontWeight={600}
              color='text.secondary'
              sx={{ mb: 0.5 }}
            >
              {inheritedTitle ?? 'Abgeleitet'}
            </Typography>
            <Stack spacing={0.75}>
              {inherited.map(item => (
                <Box
                  key={item.id}
                  sx={{
                    p: 1,
                    border: 1,
                    borderStyle: 'dashed',
                    borderColor: 'divider',
                    borderRadius: 1,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    opacity: item.overridden ? 0.55 : 1,
                  }}
                >
                  <Chip
                    label={item.roleLabel}
                    size='small'
                    variant='outlined'
                  />
                  <Typography
                    variant='body2'
                    sx={{
                      flex: 1,
                      textDecoration: item.overridden ? 'line-through' : 'none',
                    }}
                  >
                    {item.officeLabel}
                  </Typography>
                  <Typography variant='caption' color='text.secondary'>
                    {item.overridden ? 'durch PLZ überschrieben' : item.note}
                  </Typography>
                  <JumpButton target={item.navTarget} />
                </Box>
              ))}
            </Stack>
          </Box>
        )}
      </CardContent>

      <Dialog
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        maxWidth='xs'
        fullWidth
      >
        <DialogTitle>Stelle zuordnen — {anchorName}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {/* Schritt 1: Rolle (mit Gericht/Behörde-Kennzeichnung) */}
            <TextField
              select
              label='1. Rolle'
              value={pickerRoleId}
              onChange={e => {
                setPickerRoleId(Number(e.target.value));
                setPickerOffice(null);
              }}
              helperText={
                availableRoles.length === 0
                  ? 'Alle passenden Rollen sind bereits vergeben.'
                  : undefined
              }
            >
              {availableRoles.map(r => (
                <MenuItem key={r.idCourtRole} value={r.idCourtRole}>
                  <Stack
                    direction='row'
                    spacing={1}
                    alignItems='center'
                    sx={{ width: '100%' }}
                  >
                    <span style={{ flex: 1 }}>{r.CourtRole}</span>
                    <Chip
                      label={r.RoleCategory === 'court' ? 'Gericht' : 'Behörde'}
                      size='small'
                      color={r.RoleCategory === 'court' ? 'primary' : 'default'}
                      variant='outlined'
                    />
                  </Stack>
                </MenuItem>
              ))}
            </TextField>

            {/* Schritt 2: Stelle per Suche (gefiltert auf die Rollen-Kategorie) */}
            <Autocomplete
              options={officeOptions}
              value={pickerOffice}
              onChange={(_, v) => setPickerOffice(v)}
              getOptionLabel={o => o.name}
              isOptionEqualToValue={(o, v) =>
                o.type === v.type && o.id === v.id
              }
              disabled={typeof pickerRoleId !== 'number'}
              noOptionsText='Keine passende Stelle im Bundesland'
              renderInput={params => (
                <TextField
                  {...params}
                  label={
                    selectedRole?.RoleCategory === 'authority'
                      ? '2. Behörde'
                      : '2. Gericht'
                  }
                  placeholder='Suchen…'
                  helperText={
                    typeof pickerRoleId === 'number' &&
                    officeOptions.length === 0
                      ? 'Keine passende Stelle im Bundesland dieses Ankers vorhanden.'
                      : undefined
                  }
                />
              )}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPickerOpen(false)}>Abbruch</Button>
          <Button
            variant='contained'
            onClick={() => void submitPicker()}
            disabled={
              typeof pickerRoleId !== 'number' || !pickerOffice || isBusy
            }
          >
            {isBusy ? <CircularProgress size={18} /> : 'Zuordnen'}
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};
